import React, { useContext, useState } from 'react';
import { useStyletron } from 'baseui';
import { Block } from 'baseui/block';
import { Button, SIZE } from 'baseui/button';
import { ButtonGroup } from 'baseui/button-group';
import { Checkbox } from 'baseui/checkbox';
import { FormControl } from 'baseui/form-control';
import { Spinner } from 'baseui/icon';
import { Input } from 'baseui/input';
import { ListItem, ListItemLabel } from 'baseui/list';
import { PLACEMENT, StatefulPopover } from 'baseui/popover';
import { ButtonRow, Button as CustomButton } from '@gtintel/platform-atoms';
import { useAsyncOp } from '@gtintel/platform-utils';
import { SortableList } from './SortableList';
export const ListContext = React.createContext(null);
export function List({
  reorderable,
  inline,
  items,
  renderItem,
  onItemClick
}) {
  const [css] = useStyletron();
  const {
    reorder,
    copy,
    remove
  } = useContext(ListContext);
  const isReorderable = reorderable && !!reorder;
  const [isInline, setIsInline] = useState(true);
  const endEnhancer = item => {
    const {
      copyable,
      removeable,
      id
    } = item;
    const buttons = [];
    if (copy && copyable) {
      buttons.push(<Button key={'copy'} size="mini" kind="secondary" onClick={() => copy(id)}>
          Copy
        </Button>);
    }
    if (remove && removeable) {
      buttons.push(<StatefulPopover key={'delete'} content={() => <Block padding={'20px'} overrides={{
        Block: {
          style: ({
            $theme
          }) => ({
            backgroundColor: $theme.colors.backgroundPrimary,
            border: '1px solid #ccc',
            borderRadius: '4px'
          })
        }
      }}>
              <p>Are you sure you want to delete this item?</p>
              <Button size={SIZE.mini} onClick={() => remove(id)} overrides={{
          BaseButton: {
            style: {
              backgroundColor: '#d0021b'
            }
          }
        }}>
                Delete
              </Button>
            </Block>} placement={PLACEMENT.left} showArrow={true} overrides={{
        Arrow: {
          style: ({
            $theme
          }) => ({
            backgroundColor: $theme.colors.backgroundPrimary,
            border: '1px solid #ccc'
          })
        }
      }}>
          <Button size={SIZE.mini}>Delete</Button>
        </StatefulPopover>);
    }
    if (buttons.length === 0) return null;
    if (buttons.length === 1) return buttons[0];
    return <ButtonGroup size="mini">{buttons}</ButtonGroup>;
  };
  let list = null;
  if (isInline && renderItem) {
    list = <ul className={css({
      paddingLeft: 0,
      listStyle: 'none'
    })}>
        {items.map(item => <li key={item.id}>{renderItem(item)}</li>)}
      </ul>;
  } else {
    const listItem = item => {
      let component = item.children ?? <ListItemLabel>{item.name}</ListItemLabel>;
      if (onItemClick) {
        component = <button className={css({
          cursor: 'pointer',
          border: 'none',
          background: 'none',
          padding: '14px',
          width: '100%'
        })} data-testid="list-item-button" onClick={() => onItemClick(item.id)}>
            {component}
          </button>;
      }
      return <ListItem key={item.id} endEnhancer={() => endEnhancer(item)} overrides={{
        Content: {
          style: ({
            $theme
          }) => ({
            marginLeft: '0',
            paddingLeft: '14px'
          })
        }
      }}>
          {component}
        </ListItem>;
    };
    if (isReorderable) {
      list = <SortableList items={items.map(item => ({
        id: item.id,
        content: listItem(item)
      }))} onChange={reorder} />;
    } else {
      list = items.map(listItem);
    }
  }
  const inlineControl = <div data-testid="list-reordering-toggle" className={css({
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '14px'
  })}>
      <Checkbox checkmarkType="toggle" checked={!isInline} onChange={e => setIsInline(!e.target.checked)} labelPlacement="left">
        Reorder
      </Checkbox>
    </div>;
  return <>
      {inline && renderItem && inlineControl}
      {list}
    </>;
}
export const Add = ({
  verb
}) => {
  const ctx = React.useContext(ListContext);
  const [css] = useStyletron();
  const {
    isLoading,
    fn: add
  } = useAsyncOp(ctx.add);
  if (isLoading) return <Spinner />;
  return <div data-testid="list-add" className={css({
    padding: '1em'
  })}>
      <ButtonRow flexPosition="flex-end">
        <CustomButton kind="success" onClick={add}>
          {verb || 'Add'}
        </CustomButton>
      </ButtonRow>
    </div>;
};
export const AddWithName = ({
  verb
}) => {
  const [name, setName] = useState('');
  const ctx = useContext(ListContext);
  const [css] = useStyletron();
  const {
    isLoading,
    fn: create
  } = useAsyncOp(ctx.addWithName);
  const maybeOnAdd = async () => {
    const newName = name?.trim();
    if (newName) {
      await create(newName);
      setName('');
    }
  };
  const onKeyPress = e => {
    if (e.key === 'Enter') {
      maybeOnAdd();
    }
  };
  if (isLoading) return <Spinner />;
  return <div data-testid="list-create" className={css({
    display: 'flex',
    padding: '1em'
  })}>
      <div className={css({
      flex: '1'
    })}>
        <FormControl>
          <Input onKeyPress={onKeyPress} placeholder="Name" onChange={e => setName(e.target.value)} value={name} clearOnEscape />
        </FormControl>
      </div>
      <div className={css({
      flex: '0 0 auto',
      display: 'flex',
      margin: '0 1em',
      flexDirection: 'column',
      justifyContent: 'flex-end'
    })}>
        <FormControl>
          <Button type="submit" onClick={maybeOnAdd}>
            {verb || 'Add'}
          </Button>
        </FormControl>
      </div>
    </div>;
};